import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Box } from '@material-ui/core';
import { GetParams } from '../../../../../Helper';
import { ReskinningAutocomplete } from '../../../../../Components';
import { useTranslate } from '../../../../../Hooks';
import { useVerticalNav } from '../../../../../Contexts/VerticalNavContext';
import useStyles from './styles';
import {
  GetLookupItemsByLookupTypeName,
  propertyDetailsPut,
} from '../../../../../Services';
import Divider from '../CPP_PropertyDetailsComponent/Divider';
import CPP_AmenitiesAndFacilitiesSkeleton from '../../../../../Components/V2/CPP_AmenitiesAndFacilitiesSkeleton/CPP_AmenitiesAndFacilitiesSkeleton';
import { CompletePropertyProfileContext } from '../../../../../Contexts/CompletePropertyProfileContext/CompletePropertyProfileContext';
const filter = {
  pageIndex: 1,
  pageSize: 999,
};

function CPP_AmenitiesAndFacilitiesComponent({ propertyId, stepId, onStepComplete }) {
  const styles = useStyles();
  const { translate } = useTranslate('CompletePropertyProfile');
  const { setAlertBoxContent } = useVerticalNav();
  const context = useContext(CompletePropertyProfileContext);
  const [data, setData] = useState({
    amenities: [],
    facilities: [],
  });
  const [selected, setSelected] = useState({
    amenities: [],
    facilities: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [propertyDetails, setPropertyDetails] = useState(null);

  const getPropertyDetailsFromContext = () => {
    const propertyInfoBuffer = context?.propertyInfo;
    if (propertyInfoBuffer && Object.keys(propertyInfoBuffer).length > 0) {
      setPropertyDetails(propertyInfoBuffer);
    }
    setIsLoading(false);
  };

  async function getAllAmenities() {
    try {
      setIsLoading(true);
      const res = await GetLookupItemsByLookupTypeName({
        lookUpName: 'UnitsAmenties',
        pageSize: filter.pageSize,
        pageIndex: filter.pageIndex,
      });
      if (!(res && res.status && res.status !== 200)) {
        const lookupData = res.result || [];
        setData((prevData) => ({ ...prevData, amenities: lookupData }));

        const existingAmenities = propertyDetails?.amenities || [];
        const matchedAmenities = lookupData.filter((item) =>
          existingAmenities.some(
            (selectedItem) => selectedItem.lookupItemId === item.lookupItemId
          )
        );

        setSelected((prevData) => ({
          ...prevData,
          amenities: matchedAmenities,
        }));
      } else {
        setData((prevData) => ({ ...prevData, amenities: [] }));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }
  async function getAllFacilities() {
    try {
      setIsLoading(true);
      const res = await GetLookupItemsByLookupTypeName({
        lookUpName: 'UnitsFacilities',
        pageSize: filter.pageSize,
        pageIndex: filter.pageIndex,
      });
      if (!(res && res.status && res.status !== 200)) {
        const lookupData = res.result || [];
        setData((prevData) => ({ ...prevData, facilities: lookupData }));

        const existingFacilities = propertyDetails?.facilities || [];
        const matchedFacilities = lookupData.filter((item) =>
          existingFacilities.some(
            (selectedItem) => selectedItem.lookupItemId === item.lookupItemId
          )
        );

        setSelected((prevData) => ({
          ...prevData,
          facilities: matchedFacilities,
        }));
      } else {
        setData((prevData) => ({ ...prevData, facilities: [] }));
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  }

  const validateForm = useCallback(() => {
    return { isValid: true, errors: [] };
  }, []);

  const onSaveClicked = useCallback(async () => {
    if (!propertyDetails) {
      return { success: false, error: 'Property details not loaded' };
    }

    const amenitiesBuffer = selected.amenities.map((item) => ({
      lookupItemId: item.lookupItemId,
      lookupItemName: item.lookupItemName,
      lookupItemCode: item.lookupItemCode,
      parentLookupTypeId: item.parentLookupTypeId,
      isEditable: item.isEditable,
      lookupItemIcon: item.lookupItemIcon,
      createdBy: item.createdBy,
      createdOn: item.createdOn,
      updatedBy: item.updatedBy || null,
      updateOn: item.updateOn || null,
      isForMobile: item.isForMobile,
    }));

    const facilitiesBuffer = selected.facilities.map((item) => ({
      lookupItemId: item.lookupItemId,
      lookupItemName: item.lookupItemName,
      lookupItemCode: item.lookupItemCode,
      parentLookupTypeId: item.parentLookupTypeId,
      isEditable: item.isEditable,
      lookupItemIcon: item.lookupItemIcon,
      createdBy: item.createdBy,
      createdOn: item.createdOn,
      updatedBy: item.updatedBy || null,
      updateOn: item.updateOn || null,
      isForMobile: item.isForMobile,
    }));

    const updatedPropertyData = {
      ...propertyDetails,
      amenities: amenitiesBuffer,
      facilities: facilitiesBuffer,
    };

    try {
      setIsLoading(true);
      const res = await propertyDetailsPut({
        id: propertyId,
        body: {
          propertyJson: {
            property: updatedPropertyData,
          },
          isLocked: updatedPropertyData?.property_lock === 'Yes' ? true : false,
        },
      });

      if (!(res && res.status && res.status !== 200)) {
        setPropertyDetails(updatedPropertyData);
        if (onStepComplete) {
          onStepComplete();
        }
        setAlertBoxContent({
          display: true,
          variant: 'success',
          title: translate('amenities-facilities-updated-successfully'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        return { success: true };
      } else {
        setAlertBoxContent({
          display: true,
          variant: 'error',
          title: translate('error-updating-amenities-facilities'),
          onClose: () => {
            setAlertBoxContent(null);
          },
        });
        return { success: false, error: 'Failed to update amenities and facilities' };
      }
    } catch (error) {
      setAlertBoxContent({
        display: true,
        variant: 'error',
        title: translate('error-updating-amenities-facilities'),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [propertyDetails, selected, propertyId, translate, setAlertBoxContent, onStepComplete]);

  const onNextClicked = useCallback(async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      return { success: false, errors: validation.errors };
    }
    
    const result = await onSaveClicked();
    if (result && result.success && context?.refetchPropertyInfo) {
      await context.refetchPropertyInfo();
    }
    
    return result;
  }, [onSaveClicked, validateForm, context]);

  useEffect(() => {
    if (propertyId && context?.isFetched) {
      getPropertyDetailsFromContext();
    }
  }, [propertyId, context?.propertyInfo, context?.isFetched]);

  useEffect(() => {
    if (propertyDetails) {
      getAllAmenities();
      getAllFacilities();
    }
  }, [propertyDetails]);

  useEffect(() => {
    if (context?.setSaveHandler) context.setSaveHandler(onSaveClicked);
    if (context?.setNextHandler) context.setNextHandler(onNextClicked);
    if (context?.setValidationHandler) context.setValidationHandler(validateForm);

    return () => {
      if (context?.setSaveHandler) context.setSaveHandler(null);
      if (context?.setNextHandler) context.setNextHandler(null);
      if (context?.setValidationHandler) context.setValidationHandler(null);
    };
  }, [onSaveClicked, onNextClicked, validateForm, context]);

  return isLoading ? (
    <CPP_AmenitiesAndFacilitiesSkeleton />
  ) : (
    <Box className={styles.CPP_AmenitiesAndFacilitiesFullWrapper}>
      <Box className={styles.CPP_AmenitiesAndFacilitiesInnerWrapper}>
        <Box className={styles.itemWrapper}>
          <Box>
            <span>{translate('amenities')}</span>
          </Box>
          <Box>
            <ReskinningAutocomplete
              idRef={'ammenitiesSelectionRef'}
              data={data.amenities}
              inputPlaceholder={translate('enter')}
              isLoading={false}
              displayLabel={(option) => option?.lookupItemName || ''}
              chipsLabel={(option) => option?.lookupItemName || ''}
              getOptionSelected={(option, value) =>
                option?.lookupItemId === value?.lookupItemId
              }
              onChange={(event, value) => {
                setSelected((prevSelected) => ({
                  ...prevSelected,
                  amenities: value,
                }));
              }}
              multiple={true}
              selectedValues={selected.amenities}
              withoutSearchButton
            />
          </Box>
        </Box>
        <Divider />
        <Box className={styles.itemWrapper}>
          <span>{translate('facilities')}</span>
          <Box>
            <ReskinningAutocomplete
              idRef={'facilitiesSelectionRef'}
              data={data.facilities}
              isLoading={false}
              inputPlaceholder={translate('enter')}
              onInputChange={(_e, value) => {}}
              multiple={true}
              displayLabel={(option) => option?.lookupItemName || ''}
              chipsLabel={(option) => option?.lookupItemName || ''}
              getOptionSelected={(option, value) =>
                option?.lookupItemId === value?.lookupItemId
              }
              selectedValues={selected.facilities}
              onChange={(event, value) => {
                setSelected((prevSelected) => ({
                  ...prevSelected,
                  facilities: value,
                }));
              }}
              withoutSearchButton
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default CPP_AmenitiesAndFacilitiesComponent;
